import { Location, NgStyle } from '@angular/common';
import { ChangeDetectorRef, Component, Input, SimpleChanges, ViewContainerRef, inject, input, signal } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';

import { MessageBus } from '@23degrees/shared/bus';
import { BaseClass } from '@23degrees/shared/classes/base.class';
import { ContentManager } from '@23degrees/shared/manager/content/content.manager';
import { MESSAGE_RECIPIENTS, REQUEST_MESSAGES, RESPONSE_MESSAGES } from '@config';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { LayoutContainerComponent } from '../../layout-container/layout-container.component';
import { ChildAnimation } from './smart-atlas3.animations';

import { AtlasChild, Content, StyleBackground } from '@23degrees/shared-code/models';
import { ResizeObserverDirective } from '@23degrees/shared/directives';
import { BackgroundBucketPipe } from '@23degrees/shared/pipes';
import { ContentDataStore } from '../../content-selector2/content-selector2.store';
import { IContentSelectorOptions, ISmartConfig } from '../../index';

@Component({
  selector: 'smart-atlas3',
  templateUrl: './smart-atlas3.component.html',
  styleUrls: ['./smart-atlas3.component.scss'],
  providers: [],
  animations: [ChildAnimation],
  imports: [BackgroundBucketPipe, NgStyle, LayoutContainerComponent, ResizeObserverDirective],
})
export class SmartAtlas3Component extends BaseClass {
  private messageBus = inject(MessageBus);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private location = inject(Location);
  private cdr = inject(ChangeDetectorRef);
  private contentManager = inject(ContentManager);
  private containerRef = inject(ViewContainerRef);

  private contentDataStore = inject(ContentDataStore);

  @Input() slug: string;
  @Input() options: IContentSelectorOptions;
  @Input() referenceID: string;

  public config = signal<ISmartConfig>({
    displayScale: 1,
    menuOpen: false,
    activeChild: null,
  });

  public busLoaded = false;

  public content = this.contentDataStore.content;
  public layout = this.contentDataStore.layout;
  public atlas = this.contentDataStore.atlas;

  public containerStyle: StyleBackground = {
    backgroundColor: '#ffffff',
  };

  public emitActiveChildContent = false;

  public nextAnimation = false;
  public nextMessage: any;

  private newContent$ = new Subject();

  ngOnInit() {
    // this.route.params.pipe(takeUntil(this.componentDestroyed$)).subscribe(this.handleRouteChange);

    this.messageBus.on(RESPONSE_MESSAGES.ContentBySlug, MESSAGE_RECIPIENTS.Atlas, this.receiveContent, this.componentDestroyed$, this.referenceID);
    this.messageBus.on(RESPONSE_MESSAGES.ContentUpdate, MESSAGE_RECIPIENTS.Atlas, this.receiveContentUpdate, this.componentDestroyed$, this.referenceID);
  }

  ngOnChanges(changes: SimpleChanges) {
    console.log('[Atlas] ngOnChanges', this.options);

    this.config.update((prev) => ({ ...prev, activeChild: null }));

    if (!this.options.editor) {
      this.messageBus.emit(REQUEST_MESSAGES.ContentBySlug, [MESSAGE_RECIPIENTS.Atlas], { slug: this.slug }, this.referenceID);
    } else {
      this.messageBus.emit(REQUEST_MESSAGES.ContentBySlug, [MESSAGE_RECIPIENTS.Atlas, MESSAGE_RECIPIENTS.Editor], { slug: this.slug }, this.referenceID);
    }
  }

  private updateContent = (content: Content) => {
    this.contentDataStore.updateContent(content);
    this.containerStyle = content.typeSpecific.style.background;
  };

  private receiveContent = (msg) => {
    const { content } = msg;
    if (!this.options.ignoreRouting) {
      this.newContent$.next(content);
      this.route.params
        .pipe(
          takeUntil(this.newContent$),
          takeUntil(this.componentDestroyed$),
          finalize(() => {
            console.log('[Atlas] destroyed routing in favour of new atlas');
          }),
        )
        .subscribe(this.handleRouteChange);
    } else {
      this.handleRouteChange({});
    }
    this.updateContent(content);
  };

  private receiveContentUpdate = (msg) => {
    const { content } = msg;
    // console.log('[Atlas] receiveContentUpdate');

    // if old content id != newContentID we need to reload our child -> cache is already update
    if (this.content()._id !== content._id) {
      for (const child of content.typeSpecific.dash.children) {
        const childContent = this.contentManager.getContent(child.slug);
        this.messageBus.emit(
          REQUEST_MESSAGES.ReloadContentBySlug,
          [MESSAGE_RECIPIENTS.ContentSelector],
          { slug: child.slug },
          childContent.localStorage.referenceID,
        );
      }
    }

    // if old content id != newContentID we need to reload our child -> cache is already update
    const childSlugs = content.typeSpecific.atlas.children.map((child) => child.slug);
    if (this.content()._id !== content._id && this.config().activeChild?.slug) {
      if (childSlugs.includes(this.config().activeChild.slug)) {
        const childContent = this.contentManager.getContent(this.config().activeChild.slug);

        this.messageBus.emit(
          REQUEST_MESSAGES.ReloadContentBySlug,
          [MESSAGE_RECIPIENTS.ContentSelector],
          { slug: this.config().activeChild.slug },
          childContent.localStorage.referenceID,
        );
      } else {
        // reloads automatically since it has a new slug does it?;
        if (content.typeSpecific.atlas.children.length > 0) {
          this.config.update((prev) => ({ ...prev, activeChild: content.typeSpecific.atlas.children[0] }));
        } else {
          this.config.update((prev) => ({ ...prev, activeChild: null }));
        }
      }
    } else {
      // check if child is still ours!
      if (!childSlugs.includes(this.config().activeChild?.slug)) {
        if (content.typeSpecific.atlas.children.length > 0) {
          this.config.update((prev) => ({ ...prev, activeChild: content.typeSpecific.atlas.children[0] }));
        } else {
          this.config.update((prev) => ({ ...prev, activeChild: null }));
        }
      }
    }

    if (!this.config().activeChild.slug && this.atlas().children.length > 0) {
      this.config.update((prev) => ({ ...prev, activeChild: this.atlas().children[0] }));
    }

    this.updateChildRoute();

    this.updateContent(content);
  };

  private handleRouteChange = (params: Params) => {
    const { mode, slug, child, editor, options } = params;

    // TODO: Embed has not child slug and only options. Lets fix this
    const childSlug = child || options;
    const { children } = this.atlas();
    const { activeChild } = this.config();

    if (childSlug && children.length > 0 && (!activeChild?.slug || activeChild?.slug !== childSlug)) {
      // console.log('[Atlas3] Got some new child', params);
      const matchingChild = children.find((obj) => obj.slug === childSlug);
      if (matchingChild) {
        this.config.update((prev) => ({ ...prev, activeChild: matchingChild }));
      } else {
        this.config.update((prev) => ({ ...prev, activeChild: children[0] }));
      }

      this.updateChildRoute();
    } else if (!activeChild && children.length > 0) {
      this.navigateToChild(children[0]);
    }
  };

  private updateChildRoute = () => {
    const { activeChild } = this.config();
    const { host, mode, slug, childSlug, editor, branch, filter } = this.globalService.getParameters();

    if (!['editor', 'embed'].includes(this.options.mode)) return;
    if (!['view', 'viewSub', 'edit', 'editSub', 'embed', 'export', 'exportSub'].includes(mode)) return;
    if (slug === this.content().slug && (!childSlug || (childSlug !== activeChild.slug && childSlug !== 'empty'))) {
      const editorType = editor && editor.substring(16, 17) !== '-' ? editor : undefined;
      const route = [mode, slug, activeChild.slug, editorType].filter((obj) => obj !== undefined);

      // console.log('>>> NAVIGATE', 'updateChildRoute Atlas', this.content, route, [mode, slug, activeChild.slug, editorType]);
      if (this.options.mode === 'embed' || mode === 'embed') {
        // this.location.go(route.join('/'));
        this.router.navigate(route, { replaceUrl: true, queryParams: { branch: branch, filter: filter }, skipLocationChange: true });
      } else {
        // WE NEED THIS UPDATE THE SLIDESHOW EDITOR
        this.router.navigate(route, { replaceUrl: true, queryParams: { branch: branch, filter: filter }, skipLocationChange: false });
      }
    }
  };

  public clickNext = () => {
    if (!this.config().activeChild?.slug) return;

    const idx = this.atlas().children.findIndex((obj) => obj.slug === this.config().activeChild.slug);
    const nextIdx = this.atlas().children.length > idx + 1 ? idx + 1 : 0;
    const child = this.atlas().children[nextIdx];

    if (this.emitActiveChildContent && this.options.editor) {
      this.emitActiveChildToEditor();
    }

    this.navigateToChild(child);
  };

  public clickPrev = () => {
    if (!this.config().activeChild?.slug) return;

    const idx = this.atlas().children.findIndex((obj) => obj.slug === this.config().activeChild.slug);
    const prevIdx = idx === 0 ? this.atlas().children.length - 1 : idx - 1;
    const child = this.atlas().children[prevIdx];

    if (this.emitActiveChildContent && this.options.editor) {
      this.emitActiveChildToEditor();
    }

    this.navigateToChild(child);
  };

  public clickMenu = () => {
    this.config.update((prev) => ({ ...prev, menuOpen: !prev.menuOpen }));
    this.updateChildRoute();
  };

  public clickChild = (obj: AtlasChild) => {
    this.config.update((prev) => ({ ...prev, activeChild: obj, menuOpen: false }));

    if (this.emitActiveChildContent && this.options.editor) {
      this.emitActiveChildToEditor();
    }

    this.updateChildRoute();
  };

  public clickEmitActiveChild = () => {
    if (!this.config().activeChild?.slug || !this.options.editor) return;
    this.emitActiveChildContent = !this.emitActiveChildContent;

    if (this.emitActiveChildContent) {
      this.emitActiveChildToEditor();
    } else {
      this.messageBus.emit(
        REQUEST_MESSAGES.ContentBySlug,
        [MESSAGE_RECIPIENTS.Editor],
        { slug: this.content().slug, options: { childEditingFinished: true } },
        this.content().localStorage.referenceID,
      );
    }

    this.updateChildRoute();
  };

  private emitActiveChildToEditor = async () => {
    const childContent = this.contentManager.getContent(this.config().activeChild.slug);

    this.messageBus.emit(
      REQUEST_MESSAGES.ContentBySlug,
      [MESSAGE_RECIPIENTS.Editor],
      { slug: this.config().activeChild.slug, options: { requestRenderData: true } },
      childContent.localStorage.referenceID,
    );
  };

  public handleLayoutEvent = (msg) => {
    // if (!msg) return;
    const { type, action } = msg;
    // console.log('type', type, msg);

    // if (type === 'click' || type === 'atlas-idx') {
    //   if (!this.nextAnimation) {
    //     this.nextAnimation = true;
    //     this.nextMessage = msg;
    //     return;
    //   }
    // }

    // if (action === 'vizReady') {
    //   this.nextAnimation = false;
    // }

    if (type === 'click' && action === 'next') {
      this.clickNext();
    }

    if (type === 'click' && action === 'prev') {
      this.clickPrev();
    }

    if (type === 'click' && action === 'menu') {
      this.config.update((prev) => ({ ...prev, menuOpen: !prev.menuOpen }));

      this.updateChildRoute();
    }

    if (type === 'atlas-idx') {
      const { idx, stayOpen } = msg;
      if (!this.config().activeChild?.slug) return;
      this.config.update((prev) => ({ ...prev, menuOpen: stayOpen }));

      if (this.emitActiveChildContent && this.options.editor) {
        this.emitActiveChildToEditor();
      }

      const child = this.atlas().children[idx];
      this.navigateToChild(child);

      // this.updateChildRoute();
    }
  };

  public resizeAtlas = (event: ResizeObserverEntry) => {
    // console.log('[Atlas] resizeAtlas');
  };

  public animationState = (event) => {
    if (event.phaseName === 'done' && event.fromState === false && event.toState === true) {
      this.handleLayoutEvent(this.nextMessage);
    }

    console.log('animationState', event);
  };

  public navigateToChild(child: AtlasChild) {
    if (!child?.slug) return;
    const { branch, filter } = this.globalService.getParameters();
    const slug = this.content().slug;
    const childSlug = child.slug;
    const params = this.route.snapshot.params;
    const mode = params.mode;
    const editor = params.editor;

    if (!this.options.ignoreRouting && (mode || this.options.mode) !== 'embed') {
      const route = [mode || this.options.mode, slug, childSlug, editor].filter((param) => !!param);
      this.router.navigate(route, { queryParams: { branch: branch, filter: filter } });
    } else {
      this.config.update((prev) => ({ ...prev, activeChild: child }));
    }
  }
}
